import { Link } from 'react-router-dom';
import {
  ClockIcon,
  ClipboardIcon,
  BellAlertIcon,
  ShieldExclamationIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';

interface UrgentAttentionBarProps {
  data: {
    overdueTasks: number;
    pendingApprovals: number;
    regulatoryAlerts: number;
    openExceptions: number;
  };
  /** null = team has no data yet (empty-team state); undefined = still loading */
  healthScore?: number | null;
}

const items = [
  {
    key: 'overdueTasks' as const,
    label: 'Overdue Tasks',
    icon: ClockIcon,
    bgColor: 'bg-red-50',
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
    countColor: 'text-red-700',
    hoverBorder: 'hover:border-red-200',
    link: '/calendar?status=OVERDUE',
  },
  {
    key: 'pendingApprovals' as const,
    label: 'Pending Approvals',
    icon: ClipboardIcon,
    bgColor: 'bg-amber-50',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    countColor: 'text-amber-700',
    hoverBorder: 'hover:border-amber-200',
    link: '/pending-reviews',
  },
  {
    key: 'regulatoryAlerts' as const,
    label: 'Regulatory Alerts',
    icon: BellAlertIcon,
    bgColor: 'bg-blue-50',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    countColor: 'text-blue-700',
    hoverBorder: 'hover:border-blue-200',
    link: '/regulatory-intelligence',
  },
  {
    key: 'openExceptions' as const,
    label: 'Open Exceptions',
    icon: ShieldExclamationIcon,
    bgColor: 'bg-amber-50',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    countColor: 'text-amber-700',
    hoverBorder: 'hover:border-amber-200',
    link: '/exceptions',
  },
];

export default function UrgentAttentionBar({ data, healthScore }: UrgentAttentionBarProps) {
  const noUrgentItems =
    data.overdueTasks === 0 &&
    data.pendingApprovals === 0 &&
    data.regulatoryAlerts === 0 &&
    data.openExceptions === 0;

  // QA #4 #D: handle three states distinctly:
  //   - undefined → still loading (treat as healthy so we don't flash a banner)
  //   - null → team has no data yet (empty-team state — don't show "Needs
  //     Attention" with a misleading "50%" banner; treat as all-clear so the
  //     dashboard displays the onboarding CTA in ComplianceHealthScore)
  //   - number → real score from the backend
  const scoreIsHealthy =
    healthScore === undefined || healthScore === null || healthScore >= 80;
  const hasRealUnhealthyScore = typeof healthScore === 'number' && healthScore < 80;
  const allClear = noUrgentItems && scoreIsHealthy;

  if (noUrgentItems && hasRealUnhealthyScore) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-5 py-4">
        <ShieldExclamationIcon className="h-6 w-6 text-amber-600" />
        <div>
          <p className="text-sm font-semibold text-amber-800">Needs Attention</p>
          <p className="text-sm text-amber-600">
            No urgent items, but your compliance health score is {healthScore}% — review the breakdown below.
          </p>
        </div>
      </div>
    );
  }

  if (allClear) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-5 py-4">
        <CheckCircleIcon className="h-6 w-6 text-green-600" />
        <div>
          <p className="text-sm font-semibold text-green-800">All Clear</p>
          <p className="text-sm text-green-600">No items require your immediate attention.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
      {items.map((item) => {
        const Icon = item.icon;
        const count = data[item.key];

        return (
          <Link
            key={item.key}
            to={item.link}
            className={clsx(
              'flex items-center gap-3 rounded-lg border border-gray-100 p-4 transition-colors',
              item.bgColor,
              item.hoverBorder
            )}
          >
            <div className={clsx('rounded-lg p-2', item.iconBg)}>
              <Icon className={clsx('h-5 w-5', item.iconColor)} />
            </div>
            <div>
              <p className={clsx('text-3xl font-bold', item.countColor)}>{count}</p>
              <p className="text-sm text-gray-600">{item.label}</p>
            </div>
          </Link>
        );
      })}
    </div>
  );
}
